import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { useRef } from "react";

// Portfolio preview data - With actual screenshots
const featuredProjects = [
  {
    id: "intlrr",
    title: "INTLRR",
    category: "EdTech Platform",
    image: "https://customer-assets.emergentagent.com/job_aakaar-studio/artifacts/cq7rzh1j_Intlrr.PNG",
    description: "Finance education platform with cohort-based learning"
  },
  {
    id: "paingoriya",
    title: "Paingoriya & Associates",
    category: "CA Firm Website",
    image: "https://customer-assets.emergentagent.com/job_aakaar-studio/artifacts/okzz4rtw_paingoriya%26associates.PNG",
    description: "Chartered Accountants firm digital presence"
  },
  {
    id: "alvron-partners",
    title: "Alvron Partners",
    category: "Investment Banking",
    image: "https://images.unsplash.com/photo-1761735486587-bcac08b15c79?w=800&q=80",
    description: "Boutique investment banking firm website"
  }
];

// Testimonials - With correct info
const testimonials = [
  {
    quote: "Aakaar Designs transformed our EdTech platform into something truly special. The cohort-based learning experience they created has significantly improved student engagement and completion rates.",
    author: "INTLRR Team",
    role: "Finance Education Platform"
  },
  {
    quote: "Our CA firm needed a professional yet approachable website. Aakaar Designs delivered exactly that - a site that builds trust with clients while clearly showcasing our comprehensive services.",
    author: "Paingoriya & Associates",
    role: "Chartered Accountants, Agra"
  }
];

// Services preview
const services = [
  "Website Design",
  "UI/UX Design",
  "Brand Identity",
  "Landing Pages",
  "Custom Solutions"
];

// Parallax Image Component
function ParallaxImage({ src, alt, className }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"]
  });
  
  const y = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [1.1, 1.15, 1.1]);

  return (
    <div ref={ref} className={`overflow-hidden ${className}`}>
      <motion.img
        src={src}
        alt={alt}
        style={{ y, scale }}
        className="w-full h-full object-cover"
      />
    </div>
  );
}

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress: heroProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"]
  });
  
  const heroOpacity = useTransform(heroProgress, [0, 0.5], [1, 0]);
  const heroY = useTransform(heroProgress, [0, 0.5], [0, 100]);

  return (
    <div className="bg-[#050505]" data-testid="home-page">
      {/* Hero Section */}
      <section ref={heroRef} className="min-h-screen flex flex-col justify-center relative overflow-hidden" data-testid="hero-section">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a] via-[#050505] to-[#050505]" />
        
        {/* Subtle gold accent line */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-px h-32 bg-gradient-to-b from-[#C5A059]/50 to-transparent" />

        <motion.div 
          style={{ opacity: heroOpacity, y: heroY }}
          className="relative z-10 section-padding pt-32 md:pt-40"
        >
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1 }}
              className="max-w-5xl"
            >
              {/* Tagline */}
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.8 }}
                className="text-[#C5A059] uppercase tracking-[0.3em] text-xs md:text-sm mb-6 md:mb-8"
              >
                Premium Design Studio
              </motion.p>

              {/* Main Headline */}
              <motion.h1
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-white leading-[1.1] mb-6 md:mb-8"
              >
                We craft digital experiences that{" "}
                <span className="italic text-[#C5A059]">define</span> brands
              </motion.h1>

              {/* Subheadline */}
              <motion.p
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6, duration: 0.8 }}
                className="text-[#A1A1A1] text-base md:text-lg max-w-2xl mb-10 md:mb-12 leading-relaxed"
              >
                Aakaar Designs creates stunning websites and brand identities for founders, 
                startups, and premium brands who demand excellence.
              </motion.p>

              {/* CTAs */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8, duration: 0.8 }}
                className="flex flex-col sm:flex-row gap-4 sm:gap-6"
              >
                <a
                  href="https://wa.me/918006521008"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary inline-flex items-center justify-center gap-3"
                  data-testid="hero-whatsapp-cta"
                >
                  Chat on WhatsApp
                  <ArrowUpRight size={16} />
                </a>
                <Link
                  to="/work"
                  className="btn-secondary inline-flex items-center justify-center gap-3"
                  data-testid="hero-view-work-cta"
                >
                  View Work
                  <ArrowRight size={16} />
                </Link>
              </motion.div>
            </motion.div>
          </div>
        </motion.div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 1 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <div className="flex flex-col items-center gap-2">
            <span className="text-[#A1A1A1] text-xs uppercase tracking-widest">Scroll</span>
            <motion.div 
              animate={{ scaleY: [1, 1.5, 1] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="w-px h-12 bg-gradient-to-b from-[#A1A1A1] to-transparent origin-top" 
            />
          </div>
        </motion.div>
      </section>

      {/* Featured Work Section */}
      <section className="section-padding border-t border-white/5" data-testid="featured-work-section">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16"
          >
            <div>
              <p className="text-[#C5A059] uppercase tracking-[0.3em] text-xs mb-4">
                Selected Work
              </p>
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-white">
                Featured <span className="italic">Projects</span>
              </h2>
            </div>
            <Link
              to="/work"
              className="text-[#A1A1A1] text-sm uppercase tracking-widest hover:text-[#C5A059] transition-colors inline-flex items-center gap-2 group"
              data-testid="view-all-work-link"
            >
              View All Work
              <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>

          {/* Project Grid with Parallax */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {featuredProjects.map((project, index) => (
              <motion.div
                key={project.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.8 }}
              >
                <Link
                  to={`/work/${project.id}`}
                  className="block group"
                  data-testid={`project-card-${project.id}`}
                >
                  <div className="aspect-[4/3] overflow-hidden bg-[#0F0F0F] mb-6 relative border border-white/5 group-hover:border-[#C5A059]/30 transition-colors duration-500">
                    <motion.img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover object-top filter grayscale-[30%] group-hover:grayscale-0 transition-all duration-700"
                      whileHover={{ scale: 1.05 }}
                      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                    />
                    <div className="absolute inset-0 bg-[#C5A059]/0 group-hover:bg-[#C5A059]/5 transition-colors duration-500" />
                  </div>
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-[#A1A1A1] text-xs uppercase tracking-widest mb-2">
                        {project.category}
                      </p>
                      <h3 className="font-heading text-xl text-white group-hover:text-[#C5A059] transition-colors">
                        {project.title}
                      </h3>
                    </div>
                    <ArrowUpRight 
                      size={20} 
                      className="text-[#A1A1A1] group-hover:text-[#C5A059] group-hover:translate-x-1 group-hover:-translate-y-1 transition-all" 
                    />
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Marquee */}
      <section className="py-12 border-t border-b border-white/5 overflow-hidden" data-testid="services-marquee">
        <div className="flex whitespace-nowrap marquee">
          {[...services, ...services, ...services, ...services].map((service, index) => (
            <span
              key={index}
              className="font-heading text-2xl md:text-3xl text-white/20 mx-8 flex items-center gap-8"
            >
              {service}
              <span className="w-2 h-2 rounded-full bg-[#C5A059]" />
            </span>
          ))}
        </div>
      </section>

      {/* About Preview with Parallax */}
      <section className="section-padding" data-testid="about-preview-section">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <p className="text-[#C5A059] uppercase tracking-[0.3em] text-xs mb-4">
                About Us
              </p>
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-white mb-6">
                Design with <span className="italic">purpose</span>
              </h2>
              <p className="text-[#A1A1A1] text-base leading-relaxed mb-6">
                At Aakaar Designs, we believe that exceptional design is not just about aesthetics—
                it's about creating meaningful connections between brands and their audiences. 
                Every pixel, every interaction, every moment is crafted with intention.
              </p>
              <Link
                to="/about"
                className="text-[#C5A059] text-sm uppercase tracking-widest hover:text-white transition-colors inline-flex items-center gap-2 group"
                data-testid="learn-more-link"
              >
                Learn More
                <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <ParallaxImage
                src="https://images.unsplash.com/photo-1759563874682-30dc804d3257?w=800&q=80"
                alt="Abstract design"
                className="aspect-square bg-[#0F0F0F]"
              />
              {/* Decorative element */}
              <motion.div 
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.5, duration: 0.8 }}
                className="absolute -bottom-4 -right-4 w-32 h-32 border border-[#C5A059]/30" 
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section-padding border-t border-white/5" data-testid="testimonials-section">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <p className="text-[#C5A059] uppercase tracking-[0.3em] text-xs mb-4">
              Testimonials
            </p>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-white">
              Client <span className="italic">Voices</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.8 }}
                whileHover={{ y: -8, borderColor: "rgba(197, 160, 89, 0.3)" }}
                className="bg-[#0F0F0F] p-8 md:p-12 border border-white/5 transition-all duration-500"
                data-testid={`testimonial-${index}`}
              >
                <p className="font-accent text-xl md:text-2xl italic text-white/90 leading-relaxed mb-8">
                  "{testimonial.quote}"
                </p>
                <div>
                  <p className="text-white font-medium">{testimonial.author}</p>
                  <p className="text-[#A1A1A1] text-sm">{testimonial.role}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="section-padding border-t border-white/5" data-testid="stats-section">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
            {[
              { number: "50+", label: "Projects Delivered" },
              { number: "30+", label: "Happy Clients" },
              { number: "3+", label: "Years Experience" },
              { number: "100%", label: "Client Satisfaction" }
            ].map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.8 }}
                className="text-center"
                data-testid={`stat-${index}`}
              >
                <motion.p 
                  className="font-heading text-4xl md:text-5xl text-[#C5A059] mb-2"
                  whileInView={{ scale: [0.8, 1.1, 1] }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 + 0.3, duration: 0.6 }}
                >
                  {stat.number}
                </motion.p>
                <p className="text-[#A1A1A1] text-sm uppercase tracking-widest">
                  {stat.label}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
